import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import AudioFile from "@mui/icons-material/AudioFile";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Star from "@mui/icons-material/Star";
import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";
import Clear from "@mui/icons-material/Clear";
import { alpha, useTheme } from "@mui/material/styles";

import { KPIData } from "../../types/Call";

interface KPIDashboardProps {
  data: KPIData | null;
  loading: boolean;
  filtered: boolean;
  /** True while a smart (semantic) search is active — KPIs still reflect the classic filters, not the ranking */
  semanticMode?: boolean;
  onClearFilters?: () => void;
}

export const KPIDashboard: React.FC<KPIDashboardProps> = ({
  data,
  loading,
  filtered,
  semanticMode = false,
  onClearFilters,
}) => {
  const theme = useTheme();
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography variant="body2" color="text.secondary">
          KPI data not available
        </Typography>
      </Box>
    );
  }

  const kpiItems = [
    {
      title: "Total Calls",
      value: (data.totalFiles || 0).toLocaleString(),
      icon: <AudioFile aria-hidden="true" sx={{ fontSize: 20 }} />,
    },
    {
      title: "Average Score",
      value: data.averageScore > 0 ? `${data.averageScore.toFixed(1)}` : "N/A",
      icon: <Star aria-hidden="true" sx={{ fontSize: 20 }} />,
    },
    {
      title: "Completion Rate",
      value: `${(data.completionPercentage || 0).toFixed(1)}%`,
      icon: <CheckCircle aria-hidden="true" sx={{ fontSize: 20 }} />,
    },
    {
      title: "Audited",
      value: `${(data.autoAuditedPercentage || 0).toFixed(1)}%`,
      icon: <VerifiedOutlined aria-hidden="true" sx={{ fontSize: 20 }} />,
    },
  ];

  return (
    <Box>
      {/* Filled (not outlined) info chips so the label keeps ≥4.5:1 contrast in dark mode too.
          aria-live announces mode/filter changes without stealing focus (WCAG 4.1.3). */}
      <Box mb={2} display="flex" gap={1} alignItems="center" flexWrap="wrap" role="status" aria-live="polite">
        {semanticMode && (
          <Chip
            label="KPIs reflect filters — not smart-search ranking"
            color="info"
            variant="filled"
            size="small"
          />
        )}
        {filtered && (
          <Chip
            label="Showing filtered results"
            color="info"
            variant="filled"
            size="small"
          />
        )}
        {(filtered || semanticMode) && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Clear />}
            onClick={onClearFilters}
            sx={{ minWidth: 'auto' }}
          >
            Clear All
          </Button>
        )}
      </Box>
      <Box display="flex" gap={2}>
        {kpiItems.map((item) => (
          <Card
            key={item.title}
            sx={{
              flex: 1,
              minWidth: 0,
              borderLeft: `3px solid ${theme.palette.primary.main}`,
            }}
          >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h4"
                    component="p"
                    sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.1 }}
                  >
                    {item.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {item.title}
                  </Typography>
                </Box>
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};
